import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { memo } from "react";
import Tooltipable from "~/components/effects/tooltipable";
import { modKey, shortcut } from "../../lib/platformUtils";
import { ModalStates } from "../../states/ModalStates";
import ContextDepthSelector from "./ContextDepthSelector";
import ModelTierSelector from "./ModelTierSelector";
import OutputFormatToggle from "./OutputFormatToggle";

const insertTooltip = shortcut("Insert response", `${modKey}+Enter`);
const regenerateTooltip = shortcut(
  "Regenerate response",
  `${modKey}+Shift+Enter`,
);
const newPromptTooltip = shortcut("Start new prompt", "Esc");
const sendTooltip = shortcut("Generate message", "Enter");

const InlineToolbar = ({
  status,
  leadId,
  editorType,
  mode,
  selectedTier,
  onTierChange,
  showModelSelector,
  conversationId,
  contextDepth,
  onContextDepthChange,
  contextLoading,
  outputFormat,
  onOutputFormatChange,
  showFormatToggle,
  canSend,
  hasLimitError,
  feedbackSending,
  onGenerate,
  onStop,
  onInsert,
  onRegenerate,
  onNewPrompt,
  onRetry,
  voiceState = "idle",
  voiceSupported = false,
  onToggleVoice,
  templateFlags,
}) => {
  const isGenerating = status === ModalStates.GENERATING;
  const isFinished = status === ModalStates.FINISHED;
  const isError = status === ModalStates.ERROR;

  return (
    <div className="border-top bg-light px-2 py-1 rounded-bottom d-flex align-items-center">
      <div className="flex-grow-1">
        <div className="d-flex align-items-center flex-wrap ai-toolbar-options">
          {showModelSelector && (
            <div className="mr-2 mb-1 mb-md-0 flex-shrink-0">
              <ModelTierSelector
                selectedTier={selectedTier}
                onTierChange={onTierChange}
                conversationId={conversationId}
                isTemplateSet={!!templateFlags?.model}
              />
            </div>
          )}

          {leadId && (
            <div className="mr-2 mb-1 mb-md-0 flex-shrink-0">
              <ContextDepthSelector
                selectedDepth={contextDepth}
                onDepthChange={onContextDepthChange}
                disabled={isGenerating || contextLoading}
                loading={contextLoading}
                isTemplateSet={!!templateFlags?.context}
              />
            </div>
          )}

          {showFormatToggle && (
            <div className="mb-1 mb-md-0 flex-shrink-0">
              <OutputFormatToggle
                selectedFormat={outputFormat}
                onFormatChange={onOutputFormatChange}
                disabled={isGenerating}
                editorType={editorType}
                isTemplateSet={!!templateFlags?.format}
              />
            </div>
          )}
        </div>
      </div>

      <div className="d-flex align-items-center flex-wrap justify-content-end ml-2 flex-shrink-0">
        {isFinished && (
          <>
            <Tooltipable text={insertTooltip} placement="bottom">
              <button
                type="button"
                className="btn btn-sm btn-outline-success rounded-circle p-0 d-flex align-items-center justify-content-center flex-shrink-0 wpx28 hpx28 mr-1"
                onClick={onInsert}
                disabled={feedbackSending}
              >
                <FontAwesomeIcon icon="far fa-check" />
              </button>
            </Tooltipable>

            <Tooltipable text={regenerateTooltip} placement="bottom">
              <button
                type="button"
                className="btn btn-sm btn-outline-info rounded-circle p-0 d-flex align-items-center justify-content-center flex-shrink-0 wpx28 hpx28 mr-1"
                onClick={onRegenerate}
                disabled={feedbackSending || hasLimitError}
              >
                <FontAwesomeIcon icon="far fa-redo" />
              </button>
            </Tooltipable>

            <Tooltipable text={newPromptTooltip} placement="bottom">
              <button
                type="button"
                className="btn btn-sm btn-outline-warning rounded-circle p-0 d-flex align-items-center justify-content-center flex-shrink-0 wpx28 hpx28 mr-1"
                onClick={onNewPrompt}
                disabled={feedbackSending}
              >
                <FontAwesomeIcon icon="far fa-plus" />
              </button>
            </Tooltipable>
          </>
        )}

        {isError && (
          <Tooltipable text="Retry request" placement="bottom">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary rounded-circle p-0 d-flex align-items-center justify-content-center flex-shrink-0 wpx28 hpx28 mr-1"
              onClick={onRetry}
            >
              <FontAwesomeIcon icon="far fa-sync" />
            </button>
          </Tooltipable>
        )}

        {voiceSupported && !isGenerating && !isError && (
          <Tooltipable
            text={
              voiceState === "listening"
                ? "Click to stop voice typing"
                : "Click to start voice typing"
            }
            placement="bottom"
          >
            <button
              type="button"
              className={`btn btn-sm rounded-circle p-0 d-flex align-items-center justify-content-center flex-shrink-0 wpx28 hpx28 mr-1 ${
                voiceState === "listening"
                  ? "btn-danger"
                  : "btn-outline-secondary"
              }`}
              onClick={onToggleVoice}
              disabled={hasLimitError}
            >
              <FontAwesomeIcon
                icon={
                  voiceState === "listening"
                    ? "fas fa-microphone"
                    : "fal fa-microphone"
                }
                beatFade={voiceState === "listening"}
              />
            </button>
          </Tooltipable>
        )}

        {!isError && isGenerating && (
          <Tooltipable text="Stop generating" placement="bottom">
            <button
              type="button"
              className="btn btn-sm btn-outline-danger rounded-circle p-0 d-flex align-items-center justify-content-center flex-shrink-0 wpx28 hpx28"
              onClick={onStop}
            >
              <FontAwesomeIcon icon="far fa-stop-circle" beatFade />
            </button>
          </Tooltipable>
        )}

        {!isError && !isGenerating && (
          <Tooltipable text={sendTooltip} placement="bottom">
            <button
              type="button"
              className="btn btn-sm btn-success rounded-circle p-0 d-flex align-items-center justify-content-center flex-shrink-0 wpx28 hpx28"
              onClick={onGenerate}
              disabled={!canSend}
            >
              <FontAwesomeIcon icon="far fa-arrow-up" />
            </button>
          </Tooltipable>
        )}
      </div>
    </div>
  );
};

InlineToolbar.propTypes = {
  status: PropTypes.string.isRequired,
  leadId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  editorType: PropTypes.string,
  mode: PropTypes.string,
  selectedTier: PropTypes.string.isRequired,
  onTierChange: PropTypes.func.isRequired,
  showModelSelector: PropTypes.bool,
  conversationId: PropTypes.number,
  contextDepth: PropTypes.string.isRequired,
  onContextDepthChange: PropTypes.func.isRequired,
  contextLoading: PropTypes.bool,
  outputFormat: PropTypes.string.isRequired,
  onOutputFormatChange: PropTypes.func.isRequired,
  showFormatToggle: PropTypes.bool,
  canSend: PropTypes.bool,
  hasLimitError: PropTypes.bool,
  feedbackSending: PropTypes.bool,
  onGenerate: PropTypes.func.isRequired,
  onStop: PropTypes.func,
  onInsert: PropTypes.func.isRequired,
  onRegenerate: PropTypes.func.isRequired,
  onNewPrompt: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  voiceState: PropTypes.string,
  voiceSupported: PropTypes.bool,
  onToggleVoice: PropTypes.func,
  templateFlags: PropTypes.shape({
    model: PropTypes.bool,
    context: PropTypes.bool,
    format: PropTypes.bool,
  }),
};

export default memo(InlineToolbar);
